import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { Fade, useScrollTrigger } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Divider } from '_lib/components/Divider/Divider';
import { Fab } from '_lib/components/Fab/Fab';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { Paper } from '_lib/components/Paper/Paper';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { AppDrawerState, AppRouteContext } from '../App/app.types';

import { ApiInfo } from './ApiInfo';
import { AppConfigManagement } from './AppConfigManagement';
import { NationalNumbersConfigManagement } from './NationalNumbersConfigManagement';
import { ServiceTitanConfigManagement } from './ServiceTitanConfigManagement';

type SystemAdministrationProps = {
  appDrawerState?: AppDrawerState;
};

export const SystemAdministration = (props: SystemAdministrationProps) => {
  const { appDrawerState } = props;

  const [scrollTarget, setScrollTarget] = useState<HTMLDivElement | undefined>(undefined);
  const scrollTrigger = useScrollTrigger({ target: scrollTarget, disableHysteresis: true, threshold: 0 });

  const api = useContext(ApiContext);

  const appConfigQuery = api.appConfig.useAppConfigQuery();

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <SimpleAppBar title='System Administration' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <Divider />
      <FlexBox sx={{ overflow: 'hidden', height: '100%', width: '100%', position: 'relative' }}>
        <BackdropSpinner open={appConfigQuery.isFetching} />
        <Paper
          ref={(node) => {
            if (node) {
              setScrollTarget(node);
            }
          }}
          variant='elevation'
          elevation={3}
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
            borderRadius: 0,
            padding: 3,
            width: '100%',
            height: '100%',
            overflow: 'auto'
          })}>
          <Stack useFlexGap spacing={2}>
            <Card sx={{ display: 'flex', flexDirection: 'column' }}>
              <PrimaryCardHeader title='Api Info' />
              <ApiInfo />
            </Card>
            {appConfigQuery.error && (
              <ManualErrorBoundary
                errorState={{ error: appConfigQuery.error, message: 'Error fetching App Config' }}
                onRefresh={async () => {
                  appConfigQuery.refetch();
                }}
                sx={{ height: 'fit-content' }}
              />
            )}
            {!appConfigQuery.error && appConfigQuery.data && (
              <>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                  <PrimaryCardHeader title='App Config Management' />
                  <AppConfigManagement appConfigDetail={appConfigQuery.data} />
                </Card>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                  <PrimaryCardHeader title='Service Titan Config Management' />
                  <ServiceTitanConfigManagement appConfigDetail={appConfigQuery.data} />
                </Card>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                  <PrimaryCardHeader title='National Numbers Management' />
                  <NationalNumbersConfigManagement appConfigDetail={appConfigQuery.data} />
                </Card>
              </>
            )}
          </Stack>
          <Fade in={scrollTrigger}>
            <Tooltip title='Scroll to top'>
              <Fab
                size='small'
                sx={{ position: 'sticky', bottom: '8px', alignSelf: 'flex-end' }}
                onClick={() => {
                  scrollTarget?.scrollTo(0, 0);
                }}>
                <KeyboardArrowUpIcon />
              </Fab>
            </Tooltip>
          </Fade>
        </Paper>
      </FlexBox>
    </FlexBox>
  );
};

export const SystemAdministrationRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();

  return <SystemAdministration appDrawerState={appDrawerState} />;
};
